import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  FaUser, 
  FaCalendarAlt, 
  FaMoneyBillWave,
  FaQrcode,
  FaUpload,
  FaCheckCircle,
  FaTimes,
  FaInfoCircle
} from 'react-icons/fa';
import { publicWorkshopAPI } from '../../services/api';
import { formatDateToIST } from '../../utils/dateUtils';

const PublicWorkshopForm = () => {
  const { publicLinkId } = useParams();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [workshop, setWorkshop] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [paymentScreenshot, setPaymentScreenshot] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    course: '',
    year: ''
  });



  const yearOptions = [
    { value: '', label: 'Select your year' },
    { value: '1st Year', label: '1st Year' },
    { value: '2nd Year', label: '2nd Year' },
    { value: '3rd Year', label: '3rd Year' },
    { value: '4th Year', label: '4th Year' },
    { value: 'Working Professional', label: 'Working Professional' },
    { value: 'Other', label: 'Other' }
  ];

  useEffect(() => {
    fetchWorkshop();
  }, [publicLinkId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchWorkshop = async () => {
    try {
      setLoading(true);
      const response = await publicWorkshopAPI.getByPublicLink(publicLinkId);
      setWorkshop(response.data);
    } catch (error) {
      console.error('Error fetching workshop:', error);
      setError('This workshop is not available or has expired.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file (JPG, PNG, etc.)');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }
      
      setPaymentScreenshot(file);
      setError('');
    }
  };

  const convertFileToBytes = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        // Convert to base64 string for JSON serialization
        const base64String = reader.result.split(',')[1]; // Remove data:image/...;base64, prefix
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const isFormComplete = () => {
    // Check all required fields are filled
    const requiredFields = ['name', 'email', 'phone', 'course', 'year'];
    for (const field of requiredFields) {
      if (!formData[field].trim()) {
        return false;
      }
    }

    // Check email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      return false;
    }

    // Check phone format
    const phoneRegex = /^[+]?[1-9][\d]{0,15}$/;
    if (!phoneRegex.test(formData.phone.replace(/[\s\-()]/g, ''))) {
      return false;
    }

    // Check payment screenshot is uploaded only for paid workshops
    if (workshop?.is_paid && !paymentScreenshot) {
      return false;
    }

    return true;
  };

  const validateForm = () => {
    const requiredFields = ['name', 'email', 'phone', 'course', 'year'];
    for (const field of requiredFields) {
      if (!formData[field].trim()) {
        setError(`${field.charAt(0).toUpperCase() + field.slice(1)} is required`);
        return false;
      }
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    // Basic phone validation
    const phoneRegex = /^[+]?[1-9][\d]{0,15}$/;
    if (!phoneRegex.test(formData.phone.replace(/[\s\-()]/g, ''))) {
      setError('Please enter a valid phone number');
      return false;
    }

    // Payment screenshot required only for paid workshops
    if (workshop?.is_paid && !paymentScreenshot) {
      setError('Please upload a payment screenshot');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      
      // Prepare registration data
      const registrationData = { ...formData };
      
      // Only include payment screenshot for paid workshops
      if (workshop?.is_paid && paymentScreenshot) {
        const imageBytes = await convertFileToBytes(paymentScreenshot);
        registrationData.payment_screenshot = imageBytes;
        registrationData.payment_screenshot_filename = paymentScreenshot.name;
        registrationData.payment_screenshot_content_type = paymentScreenshot.type;
      }
      
      console.log('Submitting registration with data:', {
        ...registrationData,
        payment_screenshot: registrationData.payment_screenshot ? `[${registrationData.payment_screenshot.length} characters base64]` : 'Not required'
      });
      
      await publicWorkshopAPI.register(publicLinkId, registrationData);
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting registration:', error);
      
      // Handle validation errors from backend
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          // Extract error messages from validation errors
          const errorMessages = detail.map(err => err.msg).join(', ');
          setError(errorMessages);
        } else {
          setError(detail);
        }
      } else {
        setError('Failed to submit registration. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    return formatDateToIST(dateString);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center">
        <div className="text-center">
          {/* Neurons Logo */}
          <img 
            src="/neurons-logo.avif" 
            alt="Neurons School of Innovation and AI" 
            className="h-12 mx-auto mb-6"
          />
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-300">Loading workshop details...</p>
        </div>
      </div>
    );
  }

  if (error && !workshop) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md mx-4 text-center">
          {/* Neurons Logo */}
          <img 
            src="/neurons-logo.avif" 
            alt="Neurons School of Innovation and AI" 
            className="h-12 mx-auto mb-4"
          />
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaTimes className="text-2xl text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Workshop Not Available</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="space-y-3">
            <p className="text-sm text-gray-500">Please contact us directly:</p>
            <p className="text-blue-600 font-medium">info@neuronschool.in</p>
            <p className="text-blue-600 font-medium">+91-9876543210</p>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-xl p-8 max-w-md mx-4 text-center">
          {/* Neurons Logo */}
          <img 
            src="/neurons-logo.avif" 
            alt="Neurons School of Innovation and AI" 
            className="h-12 mx-auto mb-4"
          />
          <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaCheckCircle className="text-2xl text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">Registration Successful!</h1>
          <p className="text-gray-300 mb-6">
            Thank you for registering for <strong className="text-white">{workshop.name}</strong>. 
            {workshop.is_paid 
              ? " We'll verify your payment and contact you within 24 hours."
              : " You're all set! You'll receive a confirmation email shortly."
            }
          </p>
          <div className="bg-gray-700 border border-gray-600 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-blue-400 mb-2">What happens next?</h3>
            <ul className="text-sm text-gray-300 space-y-1 text-left">
              {workshop.is_paid ? (
                <>
                  <li>• We'll verify your payment screenshot</li>
                  <li>• You'll receive a confirmation email</li>
                  <li>• We'll contact you with workshop details</li>
                  <li>• Join us on the scheduled dates!</li>
                </>
              ) : (
                <>
                  <li>• You'll receive a confirmation email immediately</li>
                  <li>• Check your email for workshop details</li>
                  <li>• Join us on the scheduled dates!</li>
                </>
              )}
            </ul>
          </div>
          <p className="text-sm text-gray-400">
            If you have any questions, contact us at{' '}
            <a href="mailto:info@neuronschool.in" className="text-blue-400 hover:text-blue-300 hover:underline">
              info@neuronschool.in
            </a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 py-8">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            {/* Neurons Logo */}
            <div className="mb-6">
              <img 
                src="/neurons-logo.avif" 
                alt="Neurons School of Innovation and AI" 
                className="h-16 mx-auto mb-4"
              />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">
              {workshop?.name}
            </h1>
            <p className="text-gray-300">
              Complete your registration to join this workshop
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Workshop Details */}
            <div className="lg:col-span-1">
              <div className="bg-gray-800 rounded-lg shadow-lg p-6 border border-gray-700">
                <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <FaInfoCircle className="text-blue-400" />
                  Workshop Details
                </h2>
                
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <FaCalendarAlt className="text-blue-400" />
                    <div>
                      <p className="text-sm text-gray-400">Duration</p>
                      <p className="text-white font-medium">
                        {formatDate(workshop?.start_date)} - {formatDate(workshop?.end_date)}
                      </p>
                    </div>
                  </div>
                  
                  {workshop?.is_paid && (
                    <div className="flex items-center space-x-3">
                      <FaMoneyBillWave className="text-green-400" />
                      <div>
                        <p className="text-sm text-gray-400">Fees</p>
                        <p className="text-white font-medium text-lg">
                          {formatCurrency(workshop?.fees)}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                
                {workshop?.description && (
                  <div className="mt-6 pt-6 border-t border-gray-700">
                    <h3 className="text-sm font-medium text-gray-400 mb-2">Description</h3>
                    <p className="text-white text-sm">{workshop.description}</p>
                  </div>
                )}
              </div>

              {/* Payment Instructions - Only for paid workshops */}
              {workshop?.is_paid && (
                <div className="bg-gray-800 rounded-lg shadow-lg p-6 border border-gray-700 mt-6">
                  <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                    <FaQrcode className="text-green-400" />
                    Payment Instructions
                  </h2>
                  
                  <div className="space-y-4">
                    <div className="text-center">
                      <img 
                        src="/upi-qr.jpeg" 
                        alt="UPI QR Code" 
                        className="w-48 h-64 mx-auto mb-4 rounded-lg border-2 border-gray-600 shadow-lg"
                      />
                      <p className="text-sm text-gray-300 font-medium">Scan QR code to pay ₹{workshop?.fees}</p>
                    </div>
                    
                    <div className="bg-yellow-900 border border-yellow-700 rounded-lg p-4">
                      <p className="text-yellow-200 text-sm">
                        <strong>Important:</strong> Please take a screenshot of your payment confirmation and upload it below.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Registration Form */}
            <div className="lg:col-span-2">
              <div className="bg-gray-800 rounded-lg shadow-lg p-6 border border-gray-700">
                <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                  <FaUser className="text-blue-400" />
                  Registration Form
                </h2>

                {error && (
                  <div className="bg-red-900 border border-red-700 rounded-lg p-4 mb-6">
                    <p className="text-red-200">{error}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Personal Information */}
                  <div>
                    <h3 className="text-lg font-medium text-white mb-4">Personal Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400"
                          placeholder="Enter your full name"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400"
                          placeholder="your.email@example.com"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div>
                    <h3 className="text-lg font-medium text-white mb-4">Contact Information</h3>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400"
                        placeholder="+91-9876543210"
                        required
                      />
                    </div>
                  </div>

                  {/* Academic Information */}
                  <div>
                    <h3 className="text-lg font-medium text-white mb-4">Academic Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Course *
                        </label>
                        <input
                          type="text"
                          name="course"
                          value={formData.course}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400"
                          placeholder="Enter your course name"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Year *
                        </label>
                        <select
                          name="year"
                          value={formData.year}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                          required
                        >
                          {yearOptions.map(option => (
                            <option key={option.value} value={option.value} className="bg-gray-700 text-white">
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Payment Screenshot Upload - Only for paid workshops */}
                  {workshop?.is_paid && (
                    <div>
                      <h3 className="text-lg font-medium text-white mb-4">Payment Confirmation</h3>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Upload Payment Screenshot *
                        </label>
                        <div className="border-2 border-dashed border-gray-600 rounded-lg p-6 text-center">
                          <FaUpload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                            className="hidden"
                            id="payment-screenshot"
                            required
                          />
                          <label
                            htmlFor="payment-screenshot"
                            className="cursor-pointer inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                          >
                            Choose File
                          </label>
                          <p className="text-sm text-gray-400 mt-2">
                            {paymentScreenshot ? paymentScreenshot.name : 'JPG, PNG up to 5MB'}
                          </p>
                        </div>
                        {paymentScreenshot && (
                          <div className="mt-3 p-3 bg-green-900 border border-green-700 rounded-lg">
                            <p className="text-green-200 text-sm">
                              ✓ {paymentScreenshot.name} selected
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Submit Button */}
                  <div className="pt-6 border-t border-gray-600">
                    <button
                      type="submit"
                      disabled={submitting || !isFormComplete()}
                      className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors ${
                        submitting || !isFormComplete()
                          ? 'bg-gray-500 text-gray-300 cursor-not-allowed'
                          : 'bg-blue-600 hover:bg-blue-700 text-white'
                      }`}
                    >
                      {submitting ? 'Submitting...' : 'Complete Registration'}
                    </button>
                    <p className="text-xs text-gray-400 text-center mt-3">
                      By submitting this form, you agree to our terms and conditions.
                    </p>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center mt-8">
            <p className="text-gray-400">
              Need help? Contact us at{' '}
              <a href="mailto:info@neuronschool.in" className="text-blue-400 hover:underline">
                info@neuronschool.in
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicWorkshopForm; 